"use client";
import { useEffect, useState } from "react";

import { PRODUCT_INDEX_EN } from "@/constants/algolia";
import getPublicClient from "@/lib/algolia/getPublicClient";
import { ProductType as CourseType } from "@/common/course/types";
import logger from "@/lib/logger";
import { getFacetAttributeName } from "@/components/Search/utils/getFacetAttributeName";
import { AlgoliaHit } from "@/components/ProductSearch/types";
import { ProductSearchListItemProps } from "@/components/ProductSearch/components/v2/ProductSearchListItem";
import { mapAlgoliaHitsToListItems } from "./mapAlgoliaHitToListItem";
import { FACET_FETCH_LIMIT } from "@/components/Search/constants";

export interface Category {
  name: string;
  count: number;
  categoryUrl: string;
}

export interface AutocompleteResults {
  products: ProductSearchListItemProps[];
  categories: Category[];
  hasError: boolean;
}

const getProductHits = async (
  client: any,
  searchParams: { [key: string]: any },
) => {
  const params = { ...searchParams };
  const { hits, queryID } = await client.searchSingleIndex({
    indexName: PRODUCT_INDEX_EN,
    searchParams: params,
  });
  return hits.map((hit: AlgoliaHit) => ({ ...hit, queryID }));
};

const getCategoryFacets = async (
  client: any,
  query: string,
  indexName: string,
) => {
  const attributesFacetName = getFacetAttributeName("attributes", {
    indexName,
  });

  const response = await client.searchSingleIndex({
    indexName: PRODUCT_INDEX_EN,
    searchParams: {
      query: "",
      hitsPerPage: 0,
      facets: [attributesFacetName],
      maxValuesPerFacet: FACET_FETCH_LIMIT,
    },
  });

  const attributesFacets = response.facets?.[attributesFacetName] || {};

  const queryLower = query.toLowerCase();
  return Object.entries(attributesFacets)
    .filter(([name]) => name.toLowerCase().includes(queryLower))
    .map(([name, count]) => ({
      name,
      count: count as number,
      categoryUrl: `/search?attributes=${encodeURIComponent(name)}`,
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);
};

const processAutocompleteHits = (productHits: AlgoliaHit[]) => {
  // Prioritize Executive Education products (up to 2)
  const executiveEducation = productHits
    .filter((hit) => hit.product === CourseType.EXECUTIVE_EDUCATION)
    .slice(0, 2);

  const generalHits = productHits.filter(
    (hit) => hit.product !== CourseType.EXECUTIVE_EDUCATION,
  );

  const processedHits = [...executiveEducation, ...generalHits];
  return processedHits.slice(0, 20);
};

const handleError = (error: Error) => {
  const message = `Failed to fetch autocomplete results from Algolia`;
  logger.error(message, error);
};

/**
 * Hook for Algolia predictive search and autocomplete
 * Returns both product results and category suggestions based on the search query
 *
 * @param query - The search query string
 * @returns Object containing products array and categories array
 */
const useAlgoliaAutocomplete = (query: string): AutocompleteResults => {
  const client = getPublicClient();
  const [results, setResults] = useState<AutocompleteResults>({
    products: [],
    categories: [],
    hasError: false,
  });
  const indexName = PRODUCT_INDEX_EN as string;

  useEffect(() => {
    if (query) {
      const productSearchParams = {
        query,
        clickAnalytics: true,
        restrictSearchableAttributes: [
          getFacetAttributeName("title", { indexName }),
        ],
        attributesToRetrieve: [
          // Product index fields
          getFacetAttributeName("title", { indexName }),
          getFacetAttributeName("marketing_url", { indexName }),
          getFacetAttributeName("partner_keys", { indexName }),
          getFacetAttributeName("uuid", { indexName }),
          getFacetAttributeName("owners", { indexName }),
          getFacetAttributeName("weeks_to_complete", { indexName }),
          getFacetAttributeName("program_type", { indexName }),
          getFacetAttributeName("subject", { indexName }),
          // Product summary index fields
          getFacetAttributeName("productName", { indexName }),
          getFacetAttributeName("productSlug", { indexName }),
          getFacetAttributeName("productUuid", { indexName }),
          getFacetAttributeName("partnerKeys", { indexName }),
          getFacetAttributeName("partnerLogoUrl", { indexName }),
          getFacetAttributeName("weeksToComplete", { indexName }),
          getFacetAttributeName("partnerLogoOverride", { indexName }),
          getFacetAttributeName("partnerName", { indexName }),
          getFacetAttributeName("attributes", { indexName }),
          getFacetAttributeName("productType", { indexName }),
        ],
        highlightPreTag: `<em class="bg-transparent font-bold not-italic">`,
        highlightPostTag: "</em>",
        hitsPerPage: 20,
      };

      const fetchAutocomplete = async () => {
        try {
          const [productHits, categories] = await Promise.all([
            getProductHits(client, productSearchParams),
            getCategoryFacets(client, query, indexName),
          ]);

          const processedHits = processAutocompleteHits(productHits);
          const mappedProducts = mapAlgoliaHitsToListItems(processedHits);

          setResults({
            products: mappedProducts,
            categories,
            hasError: false,
          });
        } catch (error) {
          handleError(error as Error);
          setResults({ products: [], categories: [], hasError: true });
        }
      };

      fetchAutocomplete();
    } else {
      setResults({ products: [], categories: [], hasError: false });
    }
  }, [query, client, indexName]);

  return results;
};

export default useAlgoliaAutocomplete;
